import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/LangContext";
import { Buildings, SignIn, SignOut, ChartBar } from "@phosphor-icons/react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[#f9f8f6]/95 backdrop-blur-sm border-b border-[#e5e3db]"
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-home-link">
          <span className="w-9 h-9 flex items-center justify-center bg-[#b25a38] text-white">
            <Buildings size={20} weight="duotone" />
          </span>
          <div className="leading-tight">
            <div className="serif text-lg font-bold text-[#1a1a1a]">Warisan Kuching</div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#5c5c5c] -mt-0.5">
              {t("brand.tagline")}
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-2">
          <Link
            to="/"
            className={`inline-flex text-sm px-4 h-9 items-center transition-colors duration-150 ${
              location.pathname === "/"
                ? "text-[#b25a38] font-medium"
                : "text-[#1a1a1a] hover:text-[#b25a38]"
            }`}
            data-testid="nav-browse-link"
          >
            {t("nav.browse")}
          </Link>

          {isAdmin && (
            <Link
              to="/admin"
              className={`hidden sm:inline-flex text-sm px-4 h-9 items-center transition-colors duration-150 ${
                location.pathname.startsWith("/admin")
                  ? "text-[#b25a38] font-medium"
                  : "text-[#1a1a1a] hover:text-[#b25a38]"
              }`}
              data-testid="nav-admin-link"
            >
              <ChartBar size={16} className="mr-1.5" weight="duotone" />
              {t("nav.dashboard")}
            </Link>
          )}

          {/* Language toggle */}
          <div
            className="flex border border-[#e5e3db] bg-white ml-1"
            data-testid="lang-toggle"
            role="group"
            aria-label="Language"
          >
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`h-9 px-3 text-[10px] uppercase tracking-[0.2em] font-semibold border-r border-[#e5e3db] transition-colors duration-150 ${
                lang === "en" ? "bg-[#1a1a1a] text-white" : "text-[#5c5c5c] hover:text-[#1a1a1a]"
              }`}
              aria-pressed={lang === "en"}
              data-testid="lang-en-btn"
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLang("ms")}
              className={`h-9 px-3 text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors duration-150 ${
                lang === "ms" ? "bg-[#1a1a1a] text-white" : "text-[#5c5c5c] hover:text-[#1a1a1a]"
              }`}
              aria-pressed={lang === "ms"}
              data-testid="lang-ms-btn"
            >
              BM
            </button>
          </div>

          {user && user.role ? (
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 h-9 px-4 border border-[#e5e3db] text-sm text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white hover:border-[#1a1a1a] transition-colors duration-150"
              data-testid="nav-logout-btn"
              title={user.email}
            >
              <SignOut size={16} weight="duotone" />
              {t("nav.logout")}
            </button>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 h-9 px-4 bg-[#b25a38] text-white text-sm hover:bg-[#93492c] transition-colors duration-150"
              data-testid="nav-login-btn"
            >
              <SignIn size={16} weight="duotone" />
              {t("nav.login")}
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}
